import { LoadingOutlined } from '@ant-design/icons';
import { Button, message, Modal } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useGetEntityLineageQuery } from '../../../graphql/lineage.generated';
import { Direction, UpdatedLineages } from '../types';
import AddEntityEdge from './AddEntityEdge';
import LineageEntityView from './LineageEntityView';
import LineageEdges from './LineageEdges';
import { Entity, EntityType } from '../../../types.generated';
import { useUpdateLineageMutation } from '../../../graphql/mutations.generated';
import { useEntityRegistry } from '../../useEntityRegistry';
import { buildUpdateLineagePayload, recordAnalyticsEvents } from '../utils/manageLineageUtils';
import { useTranslation } from '../../../i18n/useTranslation';

const StyledModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
`;

const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 350px;
    font-size: 30px;
`;

interface Props {
    entityUrn: string;
    lineageDirection: Direction;
    closeModal: () => void;
    refetchEntity: () => void;
    setUpdatedLineages: React.Dispatch<React.SetStateAction<UpdatedLineages>>;
    showLoading?: boolean;
    entityType?: EntityType;
    entityPlatform?: string;
}

export default function ManageLineageModal({
    entityUrn,
    lineageDirection,
    closeModal,
    refetchEntity,
    setUpdatedLineages,
    showLoading,
    entityType,
    entityPlatform,
}: Props) {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const [entitiesToAdd, setEntitiesToAdd] = useState<Entity[]>([]);
    const [entitiesToRemove, setEntitiesToRemove] = useState<Entity[]>([]);
    const [updateLineage] = useUpdateLineageMutation();

    const { data, loading } = useGetEntityLineageQuery({
        variables: {
            urn: entityUrn,
            showColumns: false,
            excludeDownstream: lineageDirection === Direction.Upstream,
            excludeUpstream: lineageDirection === Direction.Downstream,
        },
    });

    function saveLineageChanges() {
        const payload = buildUpdateLineagePayload(lineageDirection, entitiesToAdd, entitiesToRemove, entityUrn);
        updateLineage({ variables: { input: payload } })
            .then((res) => {
                if (res.data?.updateLineage) {
                    closeModal();
                    if (showLoading) {
                        message.loading(t('Loading...'));
                    } else {
                        message.success(t('Updated lineage!'));
                    }
                    setTimeout(() => {
                        refetchEntity();
                        if (showLoading) {
                            message.destroy();
                            message.success(t('Updated lineage!'));
                        }
                    }, 2000);

                    setUpdatedLineages((updatedLineages) => ({
                        ...updatedLineages,
                        [entityUrn]: {
                            lineageDirection,
                            entitiesToAdd,
                            urnsToRemove: entitiesToRemove.map((entity) => entity.urn),
                        },
                    }));
                    recordAnalyticsEvents({
                        lineageDirection,
                        entitiesToAdd,
                        entitiesToRemove,
                        entityRegistry,
                        entityType,
                        entityPlatform,
                    });
                }
            })
            .catch(() => {
                message.error(t('Error updating lineage'));
            });
    }

    const isSaveDisabled = !entitiesToAdd.length && !entitiesToRemove.length;

    return (
        <StyledModal
            visible
            keyboard
            className="f-modal"
            onCancel={closeModal}
            title={
                <h1>
                    {t('Manage')} {lineageDirection} {t('Lineage')}
                </h1>
            }
            footer={
                <>
                    <Button onClick={closeModal} type="default">
                        {t('Cancel')}
                    </Button>
                    <Button onClick={saveLineageChanges} disabled={isSaveDisabled} type="primary">
                        {t('Save Changes')}
                    </Button>
                </>
            }
        >
            {loading && (
                <LoadingWrapper>
                    <LoadingOutlined />
                </LoadingWrapper>
            )}
            {!loading && (
                <>
                    {data?.entity && <LineageEntityView entity={data.entity} />}
                    <AddEntityEdge
                        lineageDirection={lineageDirection}
                        setEntitiesToAdd={setEntitiesToAdd}
                        entitiesToAdd={entitiesToAdd}
                        entityUrn={entityUrn}
                        entityType={entityType}
                    />
                    <LineageEdges
                        entity={data?.entity}
                        lineageDirection={lineageDirection}
                        entitiesToAdd={entitiesToAdd}
                        entitiesToRemove={entitiesToRemove}
                        setEntitiesToAdd={setEntitiesToAdd}
                        setEntitiesToRemove={setEntitiesToRemove}
                    />
                </>
            )}
        </StyledModal>
    );
}
